import { Injectable } from '@angular/core';
import { CreateCardError, CreateCardToken, CreditCardInfo } from '@app/core/models/openpay.model';
import { environment } from '@environment';
import { Observable } from 'rxjs';

declare let OpenPay: any;

@Injectable({
  providedIn: 'root',
})
export class OpenpayService {
  constructor() {
    OpenPay.setId(environment.openpay_merchant_id);
    OpenPay.setApiKey(environment.openpay_public_api_key);
    OpenPay.setSandboxMode(environment.openpay_sandbox_mode);
    const sandBoxMode: boolean = OpenPay.getSandboxMode();
    if (sandBoxMode) console.warn('OpenPay: SandboxMode Enable');
  }

  public createToken(creditCardInfo: CreditCardInfo): Observable<CreateCardToken> {
    return new Observable((observer) => {
      const startTime = new Date().getTime();
      OpenPay.token.create(
        creditCardInfo,
        ({ data }: { data: CreateCardToken }) => {
          const endTime = new Date().getTime();
          const responseTime = (endTime - startTime) / 1000;
          const data_response = {
            ...data,
            response_time: responseTime,
          };
          observer.next(data_response);
        },
        (error: CreateCardError) => {
          observer.error(error);
        }
      );
    });
  }

  /**
   * TRUE. Número de tarjeta válido y aceptado por OpenPay (MASTERCARD)
   * FALSE. Número de tarjeta válido pero no aceptado por OpenPay (AMEX)
   * @param {number} cardNumber
   * @returns {boolean} boolean
   */
  public validateCardNumber(cardNumber: number): boolean {
    if (!cardNumber) return;
    return OpenPay.card.validateCardNumber(cardNumber);
  }

  /**
   * Validate the cvc
   * @param {number} cvc
   * @returns {boolean} boolean
   */
  public validateCVC(cvc: number): boolean {
    if (!cvc) return;
    return OpenPay.card.validateCVC(cvc);
  }

  /**
   * Validate the cvc is valid for card type
   *
   * Examples:
   * validateCVCAndCardNumber('123','5555555555554444')
   * TRUE, tarjeta MASTERCARD y CVC de longitud 3
   *
   * validateCVCAndCardNumber('124','5555555555554444')
   * FALSE, tarjeta MASTERCARD y CVC de longitud 4
   *
   * validateCVCAndCardNumber('1234','378282246310005')
   * TRUE, tarjeta AMERICAN EXPRESS y CVC de longitud 4
   *
   * validateCVCAndCardNumber('123','378282246310005')
   * FALSE, tarjeta AMERICAN EXPRESS y CVC de longitud 3
   * @param {number} cvc
   * @param {number} cardNumber
   * @returns {boolean} boolean
   */
  public validateCVCAndCardNumber(cvc: number, cardNumber: number): boolean {
    if (!cvc || !cardNumber) return;
    return OpenPay.card.validateCVC(cvc, cardNumber);
  }

  /**
   * Validate the expiration date
   * @param {number} month
   * @param {number} year
   * @returns {boolean} boolean
   */
  public validateExpiry(month: number, year: number): boolean {
    if (!month || !year) return;
    return OpenPay.card.validateExpiry(month, year);
  }

  /**
   * get card type like a string
   *
   * examples
   * getCardType('5555555555554444')
   * Mastercard
   *
   * getCardType('4111111111111111')
   * Visa Electron
   *
   * getCardType('378282246310005')
   * American Express
   *
   * getCardType('30569309025904')
   * Diners Club Carte Blanche
   *
   * getCardType('6011111111111117')
   * Discover
   *
   * getCardType('3530111333300000')
   * JCB
   * @param {number} cardNumber
   * @returns {string} string - card type
   */
  public getCardType(cardNumber: number): string {
    if (!cardNumber) return;
    return OpenPay.card.cardType(cardNumber);
  }

  public getDeviceData(): string {
    return OpenPay.deviceData.setup();
  }
}
